/** Email automations (one per kind): enable, settings, steps (delay + optional custom content), preview, test. */
import React, { useCallback, useEffect, useState } from 'react';
import { Button, Card, Col, Collapse, Drawer, Empty, Input, InputNumber, Row, Segmented, Space, Spin, Switch, Tag, Typography, message } from 'antd';
import { DeleteOutlined, EyeOutlined, PlusOutlined } from '@ant-design/icons';
import { useTranslation } from '../../../shared/i18n';
import { listOf, money, useSite } from './common';
import { BlockEditor, PreviewModal, TestModal, mktError } from './MarketingCommon';

const { Text, Paragraph } = Typography;
export const KINDS = {
    abandoned_checkout: { label: 'Abandoned checkout', desc: 'Remind shoppers who left items in their cart' },
    welcome: { label: 'Welcome', desc: 'Greet new subscribers' },
    post_purchase: { label: 'After purchase', desc: 'Thank buyers and ask for a review' },
    winback: { label: 'Win-back', desc: 'Bring back customers who have not ordered in a while' },
    birthday: { label: 'Birthday', desc: 'Send a birthday greeting' },
};

const fmtDelay = (h, t) => (h === 0 ? t('Immediately') : h % 24 === 0 ? t('After {{n}} days', { n: h / 24 }) : t('After {{n}} hours', { n: h }));

const Flow = ({ kind, onClose, onSaved, goDomain }) => {
    const { t } = useTranslation();
    const { api } = useSite();
    const [a, setA] = useState(null);
    const [dirty, setDirty] = useState(false);
    const [saving, setSaving] = useState(false);
    const [preview, setPreview] = useState(null);
    const [test, setTest] = useState(null);
    useEffect(() => { api.get(`/marketing/automations/${kind}`).then(setA).catch((e) => { mktError(e); onClose(); }); }, [kind]); // eslint-disable-line react-hooks/exhaustive-deps
    const set = (patch) => { setA((x) => ({ ...x, ...patch })); setDirty(true); };
    const setStep = (i, patch) => set({ steps: a.steps.map((s, j) => (j === i ? { ...s, ...patch } : s)) });
    const body = (over = {}) => ({
        enabled: a.enabled, settings: a.settings,
        steps: a.steps.map((s) => ({ delay_hours: s.delay_hours, subject: s.subject || '', preheader: s.preheader || '', content: s.content || null })),
        ...over,
    });
    const save = async (over) => {
        setSaving(true);
        try {
            const r = await api.put(`/marketing/automations/${kind}`, body(over));
            setA(r);
            setDirty(false);
            onSaved();
            return true;
        } catch (e) { mktError(e, { goDomain }); return false; } finally { setSaving(false); }
    };
    if (!a) return <div style={{ padding: 60, textAlign: 'center' }}><Spin /></div>;
    const st = a.settings || {};
    return (
        <>
            <Space style={{ marginBottom: 16 }} wrap>
                <Switch checked={a.enabled} onChange={async (enabled) => { if (await save({ enabled })) message.success(enabled ? t('On') : t('Off')); }} loading={saving} />
                <Text strong>{a.enabled ? t('On') : t('Off')}</Text>
                {a.active_runs > 0 && <Tag>{t('{{n}} in progress', { n: a.active_runs })}</Tag>}
                <div style={{ flex: 1 }} />
                <Button type="primary" disabled={!dirty} loading={saving} onClick={() => save()}>{t('Save')}</Button>
            </Space>
            {kind === 'abandoned_checkout' || kind === 'post_purchase' ? (
                <Space wrap style={{ marginBottom: 12 }}>
                    <Text>{t('Send to')}</Text>
                    <Segmented size="small" value={st.audience || 'subscribers'} onChange={(audience) => set({ settings: { ...st, audience } })}
                        options={[{ value: 'subscribers', label: t('Subscribers') }, { value: 'all', label: t('Everyone not unsubscribed') }]} />
                    {kind === 'abandoned_checkout' && <InputNumber size="small" min={0} value={st.min_total || 0} onChange={(min_total) => set({ settings: { ...st, min_total: min_total || 0 } })} addonBefore={t('Min. cart (minor units)')} />}
                    {kind === 'post_purchase' && (
                        <Segmented size="small" value={st.anchor || 'fulfilled'} onChange={(anchor) => set({ settings: { ...st, anchor } })}
                            options={[{ value: 'fulfilled', label: t('After shipping') }, { value: 'delivered', label: t('After delivery') }]} />
                    )}
                </Space>
            ) : null}
            {kind === 'winback' && <InputNumber min={30} max={730} value={st.days || 90} onChange={(days) => set({ settings: { ...st, days } })} addonBefore={t('No order for')} addonAfter={t('days')} style={{ marginBottom: 12 }} />}
            {a.steps.map((s, i) => (
                <Card key={i} size="small" style={{ marginBottom: 8 }}
                    title={<Space><Text strong>{t('Email {{n}}', { n: i + 1 })}</Text><InputNumber size="small" min={0} max={2160} value={s.delay_hours} onChange={(delay_hours) => setStep(i, { delay_hours: delay_hours || 0 })} addonAfter={t('hours')} style={{ width: 130 }} /><Text type="secondary" style={{ fontSize: 12 }}>{fmtDelay(s.delay_hours, t)}</Text></Space>}
                    extra={(
                        <Space size={0}>
                            <Button size="small" type="text" icon={<EyeOutlined />} onClick={async () => { if (dirty && !(await save())) return; setPreview(i); }}>{t('Preview')}</Button>
                            <Button size="small" type="text" onClick={async () => { if (dirty && !(await save())) return; setTest(i); }}>{t('Send test')}</Button>
                            {a.steps.length > 1 && <Button size="small" type="text" danger icon={<DeleteOutlined />} onClick={() => set({ steps: a.steps.filter((_, j) => j !== i) })} />}
                        </Space>
                    )}>
                    <Space direction="vertical" style={{ width: '100%' }}>
                        <Input addonBefore={t('Subject')} value={s.subject || ''} placeholder={s.default?.subject} onChange={(e) => setStep(i, { subject: e.target.value })} />
                        <Input addonBefore={t('Preview text')} value={s.preheader || ''} placeholder={s.default?.preheader} onChange={(e) => setStep(i, { preheader: e.target.value })} />
                        <Collapse ghost size="small" items={[{
                            key: 'c',
                            label: s.content ? t('Custom content') : t('Built-in content (in each customer\'s language)'),
                            children: (
                                <>
                                    {s.content
                                        ? <Button size="small" danger style={{ marginBottom: 8 }} onClick={() => setStep(i, { content: null })}>{t('Use built-in content')}</Button>
                                        : <Button size="small" style={{ marginBottom: 8 }} onClick={() => setStep(i, { content: s.default?.content || { blocks: [{ type: 'text', text: '' }] } })}>{t('Customize')}</Button>}
                                    {s.content && <BlockEditor value={s.content} onChange={(content) => setStep(i, { content })} />}
                                </>
                            ),
                        }]} />
                    </Space>
                </Card>
            ))}
            {a.steps.length < 5 && <Button type="dashed" block icon={<PlusOutlined />} onClick={() => set({ steps: [...a.steps, { delay_hours: (a.steps[a.steps.length - 1]?.delay_hours || 0) + 24, subject: '', preheader: '', content: null }] })}>{t('Add email')}</Button>}
            <PreviewModal open={preview !== null} onClose={() => setPreview(null)} loader={() => api.post(`/marketing/automations/${kind}/preview`, { step: preview })} />
            <TestModal open={test !== null} onClose={() => setTest(null)} onSend={async (to) => { try { await api.post(`/marketing/automations/${kind}/test`, { step: test, to }); } catch (e) { mktError(e, { goDomain }); throw e; } }} />
        </>
    );
};

const MarketingAutomations = ({ goDomain }) => {
    const { t } = useTranslation();
    const { api } = useSite();
    const [list, setList] = useState(null);
    const [open, setOpen] = useState(null);
    const load = useCallback(() => { api.get('/marketing/automations').then((d) => setList(listOf(d))).catch((e) => { setList([]); mktError(e); }); }, [api]);
    useEffect(() => { load(); }, [load]);
    if (list === null) return <div style={{ padding: 40, textAlign: 'center' }}><Spin /></div>;
    if (!list.length) return <Empty />;
    return (
        <>
            <Row gutter={[12, 12]}>
                {list.map((a) => (
                    <Col key={a.kind} xs={24} md={12} xl={8}>
                        <Card size="small" hoverable onClick={() => setOpen(a.kind)} style={{ height: '100%' }}>
                            <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                                <Text strong>{t(KINDS[a.kind]?.label || a.kind)}</Text>
                                <Tag color={a.enabled ? 'green' : 'default'}>{a.enabled ? t('On') : t('Off')}</Tag>
                            </Space>
                            <Paragraph type="secondary" style={{ fontSize: 12, margin: '4px 0 8px' }}>{t(KINDS[a.kind]?.desc || '')}</Paragraph>
                            <Space size={12} wrap style={{ fontSize: 12 }}>
                                <span>{t('Sent', { context: 'shop' })}: {a.stats?.sent || 0}</span>
                                <span>{t('Opened')}: {a.stats?.opened || 0}</span>
                                <span>{t('Orders')}: {a.stats?.orders || 0}</span>
                                <span>{money(a.stats?.revenue || 0, a.stats?.currency)}</span>
                            </Space>
                        </Card>
                    </Col>
                ))}
            </Row>
            <Drawer open={!!open} onClose={() => { setOpen(null); load(); }} width={Math.min(860, window.innerWidth)} title={open ? t(KINDS[open]?.label || open) : ''} destroyOnHidden>
                {open && <Flow kind={open} onClose={() => setOpen(null)} onSaved={load} goDomain={() => { setOpen(null); goDomain?.(); }} />}
            </Drawer>
        </>
    );
};

export default MarketingAutomations;
